/**
 * Wrapper de fetch pra API PEP-VIDA.
 *
 * PROMPT 06: auth via cookie httpOnly. O browser envia o cookie automaticamente
 * em toda request cross-origin (com `credentials: 'include'`). NÃO usamos
 * localStorage, sessionStorage nem Authorization header — JS nunca toca o JWT.
 *
 * Em prod (HTTPS) isso é seguro: cookie httpOnly é imune a XSS, e SameSite=Lax
 * protege contra CSRF na maioria dos casos.
 */

const browserOrigin =
  typeof window !== 'undefined' ? window.location.origin : '';

const API_URL =
  browserOrigin.includes('resultado.vidavg.com.br')
    ? `${browserOrigin}/pep-vida`
    : import.meta.env.VITE_API_URL || browserOrigin || 'http://127.0.0.1:3001';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  signal?: AbortSignal;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: ApiOptions & { body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  // Sem Authorization header — o cookie httpOnly é enviado automaticamente
  // via `credentials: 'include'`.

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include', // envia cookie httpOnly automaticamente
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    const body = isJson ? await res.json().catch(() => ({})) : await res.text();
    const message =
      (isJson && (body as { message?: string }).message) ||
      res.statusText ||
      `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }

  if (!isJson) {
    return (await res.text()) as unknown as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts: ApiOptions = {}) =>
    request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts: ApiOptions = {}) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts: ApiOptions = {}) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts: ApiOptions = {}) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts: ApiOptions = {}) =>
    request<T>('DELETE', path, opts),
};

export const apiUrl = API_URL;
